import type * as React from 'react';

import type { ChartProps } from '@coursera-web/cds-labs';
import type Maybe from 'graphql/tsutils/Maybe';

export type MenuItem = {
  name: string;
  /** Override slug sent to native analytics GQL when the route name differs from the BE spec slug.
   *  Only needed for license-only orgs: route "utilization" → spec "utilization-licenses". */
  nativeSpecSlug?: string;
  /** Translated display string — wrap source string in _t() at call site before storing here. */
  label: string;
  hasLink: boolean;
  children?: MenuItem[];
};

export type MenuItems = MenuItem[];

export const REPORT_TYPE = {
  LEARNER_ENGAGEMENT: 'LEARNER_ENGAGEMENT',
  LEARNER_SKILLS: 'LEARNER_SKILLS',
  LEARNER_PERFORMANCE: 'LEARNER_PERFORMANCE',
  CURRICULUM: 'CURRICULUM',
  MONTHLY_DIGEST_EMAIL: 'MONTHLY_DIGEST_EMAIL',
  VERIFIED_SKILL_PATH_PROGRESS: 'VERIFIED_SKILL_PATH_PROGRESS',
  INTERACTIVE_ITEMS_AND_LABS: 'INTERACTIVE_ITEMS_AND_LABS',
} as const;

export type ReportType = (typeof REPORT_TYPE)[keyof typeof REPORT_TYPE];

export type FilterType = 'date_range' | 'multi_select' | 'single_select' | 'checkbox';

// date_range stores: ["<preset>"] or ["custom", "<startDate>", "<endDate>"]
// multi_select stores: [] (all selected) or ["id1", "id2", ...]
export type FilterValues = Record<string, string[]>;

// Prefixed to avoid shadowing React.ComponentType in files that import both.
export type DashboardComponentType = 'chart' | 'kpi' | 'table';

export type ComponentLayout = {
  row: number;
  column: number; // 0-based, within a 12-column grid
  width: number; // 1–12 columns wide
  height: number; // row units (1 unit = GRID_ROW_HEIGHT_PX in the skeleton)
};

export type DashboardComponent = {
  id: string;
  componentType: DashboardComponentType;
  /** Highcharts chart type — required for 'chart' components, ignored for kpi/table. */
  chartType?: 'bar' | 'column' | 'line' | 'pie';
  /** When true, column/bar charts stack series rather than grouping them side-by-side. */
  stacked?: boolean;
  title?: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
  metricId?: string;
  description?: string | null;
  downloadable?: boolean | null;
  /** Default table sort, e.g. ["utilized_enrollments desc"]. First entry drives CDS Table default sort. */
  sorts?: string[];
  /** Pass-through Highcharts options from dashboard JSON (chart components only). */
  visualConfig?: VisualConfig;
  layout: ComponentLayout;
};

/** Pass-through Highcharts options from dashboard JSON (chart components only). */
export type VisualConfig = Partial<ChartProps['chartSpec']>;

export type FilterSpec = {
  id: string;
  type: FilterType;
  label: string;
  /** Single default for date_range presets (e.g. "last_30_days") */
  defaultValue?: string;
  /** Multi-value defaults for multi_select / checkbox — BE sends this in the dashboard spec */
  defaultValues?: string[];
  column?: string;
  // Static options for filters with known values (e.g. checkbox). Dynamic options come from getFilterOptions API.
  options?: FilterOption[];
};

export type FilterOption = {
  id: string;
  label: string;
  relatedIds?: Record<string, string[]>;
};

export type DashboardSpec = {
  filters: FilterSpec[];
};

export type DateRangeType =
  'LAST_30_DAYS' | 'LAST_60_DAYS' | 'LAST_90_DAYS' | 'ALL_TIME' | 'CUSTOM' | 'YEAR_TO_DATE' | 'SINCE_CONTRACT_STARTED';
export type ReportSubItem = {
  name: string;
  description?: string;
};

export type ReportV2 = {
  reportType: ReportType;
  name: string;
  description: string;
  descriptionPoints?: React.ReactNode[];
  previouslyKnownAs?: string;
  whatsNew?: {
    text: string;
    highlight?: string;
  };
  includes?: ReportSubItem[];
  isNew?: boolean;
  export?: boolean;
  actions?: boolean;
  thirdPartyOrganizationId?: string;
  contractIds?: string[];
  skillIds?: string[];
  pathwayIds?: string[];
  programIds?: string[];
  includeEnrolledWithoutActivity?: boolean;
  includeInvitedLearners?: boolean;
  includeJoinedLearners?: boolean;
  includeRemovedMembers?: boolean;
  schedule?: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  dateRangeType?: DateRangeType | null;
  startDate?: Maybe<string>;
  endDate?: Maybe<string>;
  scheduled?: boolean;
  lastModifiedAt?: Maybe<string>;
  timezone?: Maybe<string>;
  exportOneCsvPerProgram?: boolean;
  exportFormat?: ExportFormat;
  isDeleted?: Maybe<boolean>;
  schedulerName?: Maybe<string>;
};

export type ReportAction =
  | 'PREVIEW_REPORT'
  | 'CONFIGURE_REPORT'
  | 'EDIT_SCHEDULED_REPORT'
  | 'CANCEL_SCHEDULED_REPORT'
  | 'SCHEDULE_MONTHLY_DIGEST_REPORT';

export type ReportFieldConfig = {
  showContracts: boolean;
  showPrograms: boolean;
  showLearnerStatus: boolean;
  showDateRange: boolean;
  showAdditionalConfig: boolean;
  showScheduling: boolean;
  showSkills: boolean;
  showSizePreview: boolean;
  showVspFilters?: boolean;
};

export const EXPORT_FORMAT = {
  CSV: 'CSV',
  XLSX: 'XLSX',
} as const;

export type ExportFormat = (typeof EXPORT_FORMAT)[keyof typeof EXPORT_FORMAT];

// Excel spec allows 1,048,576 rows per sheet, but some spreadsheet readers
// cap at exactly 1,000,000. Using 1M ensures compatibility across readers.
// The backend (SheetWriter) enforces the same limit and rejects exports that exceed it.
export const XLSX_MAX_ROWS = 1_000_000;

export type ReportConfigFormValues = {
  contractIds?: string[];
  skillIds?: string[];
  programIds: string[];
  pathwayIds?: string[];
  dateRange: {
    type: DateRangeType;
    startDate?: string;
    endDate?: string;
  };
  includeInvitedLearners: boolean;
  includeJoinedLearners: boolean;
  includeRemovedMembers: boolean;
  oneCsvPerProgram: boolean;
  exportFormat: ExportFormat;
};

type LearnerActivityDateRange = 'thirty_days' | 'sixty_days' | 'ninety_days' | 'custom' | 'all_time';

export const DATE_RANGE_FOR_TRACKING: Record<string, LearnerActivityDateRange> = {
  LAST_30_DAYS: 'thirty_days',
  LAST_60_DAYS: 'sixty_days',
  LAST_90_DAYS: 'ninety_days',
  ALL_TIME: 'all_time',
  CUSTOM: 'custom',
};

export type ReportTrackingDataParams = {
  reportType: ReportType;
  configValues: ReportConfigFormValues;
  totalContracts: number;
  totalPrograms: number;
  totalSkills: number;
};
